import { ApolloError } from "@apollo/client";
import { Save } from "@mui/icons-material";
import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import { useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { CheckboxElement, RadioButtonGroup } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { GqlFilterType, GqlPermissions, GqlProtocol, SelectOption } from "~/__generated__";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import SelectFilter from "~/modules/app/components/GaiaFilterList/SelectFilter";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";
import ResearchTypeSelectElement from "~/shared/components/ResearchTypeSelectElement";

export type ProtocolFormValues = {
    name: string;
    description?: string | null;
    researchTypeId: string;
    templateId?: string;
    collectAllMediaPoints: "collect-all" | "collect-observations-only";
    requiresRouteShuffling: boolean;
};

type Props = {
    protocolData?: ProtocolFormValues;
    error?: ApolloError;
    onSubmit: SubmitHandler<ProtocolFormValues>;
    protocolTemplates?: Pick<GqlProtocol, "id" | "name" | "description" | "researchTypeId">[];
    templateOptions?: SelectOption[];
};

const initialData: ProtocolFormValues = {
    name: "",
    description: "",
    researchTypeId: "",
    collectAllMediaPoints: "collect-all",
    requiresRouteShuffling: true,
};

export default function ProtocolForm({
    onSubmit,
    protocolTemplates = [],
    templateOptions = [],
    protocolData = initialData,
    error = undefined,
}: Props) {
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);

    const { t } = useTranslation("protocol");
    const {
        handleSubmit,
        register,
        setError,
        control,
        reset,
        watch,
        setValue,
        formState: { errors, isLoading, isDirty, isSubmitted, isSubmitting },
    } = useForm<ProtocolFormValues>({
        values: protocolData,
    });

    const watchedTemplateId = watch("templateId");
    const nameValue = watch("name");
    const descriptionValue = watch("description");

    useEffect(() => {
        if (protocolTemplates?.length) {
            if (watchedTemplateId) {
                const selectedTemplate = protocolTemplates.find(pt => pt.id === watchedTemplateId);
                if (selectedTemplate) {
                    setValue("name", selectedTemplate.name || "");
                    setValue("description", selectedTemplate.description || "");
                    setValue("researchTypeId", selectedTemplate.researchTypeId || "");
                }
            } else {
                reset(protocolData);
            }
        }
    }, [watchedTemplateId, protocolTemplates, setValue, reset, protocolData]);

    const TemplateSelected = !!watchedTemplateId;
    const isFormSubmittable = isDirty || TemplateSelected;
    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);
    useApplyErrorToForm(error, setError);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}

                {protocolTemplates.length > 0 && (
                    <Controller
                        name="templateId"
                        control={control}
                        render={({ field }) => (
                            <SelectFilter
                                option={{
                                    __typename: "GqlFilterOptionSelect",
                                    label: t("protocolCreateScreen.templateLabel"),
                                    key: "templateId",
                                    filterType: GqlFilterType.Select,
                                    options: templateOptions,
                                }}
                                value={field.value || ""}
                                onChange={field.onChange}
                            />
                        )}
                    />
                )}

                <TextField
                    label={t("protocolForm.labelName")}
                    error={!!errors.name}
                    disabled={!isAdmin}
                    helperText={errors.name?.message}
                    slotProps={{
                        inputLabel: {
                            shrink: TemplateSelected || !!nameValue,
                        },
                    }}
                    {...register("name")}
                />

                <TextField
                    label={t("protocolForm.labelDescription")}
                    error={!!errors.description}
                    disabled={!isAdmin}
                    helperText={errors.description?.message}
                    slotProps={{
                        inputLabel: {
                            shrink: TemplateSelected || !!descriptionValue,
                        },
                    }}
                    minRows={5}
                    {...register("description")}
                    multiline
                />

                <Controller
                    control={control}
                    name="researchTypeId"
                    render={({ field }) => (
                        <ResearchTypeSelectElement {...field} disabled={!isAdmin} label={t("protocolForm.labelResearchType")} />
                    )}
                />

                <RadioButtonGroup
                    control={control}
                    disabled={!isAdmin}
                    name="collectAllMediaPoints"
                    label={t("protocolForm.labelCollectAllMediaPoints")}
                    options={[
                        { id: "collect-all", label: t("protocolForm.labelCollectAllMediaPointsCollectAll") },
                        {
                            id: "collect-observations-only",
                            label: t("protocolForm.labelCollectAllMediaPointsCollectObservationsOnly"),
                        },
                    ]}
                    required
                />

                <CheckboxElement
                    control={control}
                    name="requiresRouteShuffling"
                    label={t("protocolForm.labelRequiresRouteShuffling")}
                    disabled={!isAdmin}
                    helperText={t("protocolForm.labelRequiresRouteShufflingHelperText")}
                />

                <Button
                    sx={{ mt: 3 }}
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={!isFormSubmittable || isSubmitting}
                    loading={isLoading || isSubmitting}
                >
                    <Save />
                    {t("protocolForm.btnSubmit")}
                </Button>
            </Stack>
        </form>
    );
}
